// src/pages/admin/UserManagement.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userService, companyService } from '../../services/apiServices';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MetricsSummary from '../../components/admin/MetricsSummary';
import UserDetails from '../../components/admin/UserDetails';
import ConfirmationDialog from '../../components/admin/ConfirmationDialog';
import StatusBadge from '../../components/admin/StatusBadge';
import RoleBadge from '../../components/admin/RoleBadge';
import SearchFilterBar from '../../components/admin/SearchFilterBar';
import DataTable from '../../components/admin/DataTable';
import FormBuilder from '../../components/admin/FormBuilder';
import Pagination from '../../components/admin/Pagination';
import PasswordRequirements from '../../components/common/PasswordRequirements';
import { validatePassword } from '../../utils/passwordValidator';

const UserManagement = () => {
  // State for users and companies
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '', // Added password field for creating new users
    role: 'admin',
    companyId: '',
    status: 'active'
  });

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: ''
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Detail view state
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Fetch users and companies on component mount or when filters change
  useEffect(() => {
    fetchData();
  }, [pagination.page, pagination.limit, searchTerm, roleFilter, statusFilter, companyFilter]);

  // Function to fetch users and companies from API
  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Prepare filters for API
      const filters = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (searchTerm) filters.search = searchTerm;
      if (roleFilter !== 'all') filters.role = roleFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (companyFilter !== 'all') filters.companyId = companyFilter;

      // Fetch users with pagination and filters
      const usersResponse = await userService.getUsers(filters);
      const companiesResponse = await companyService.getCompanies({ limit: 1000 });

      if (usersResponse.success) {
        setUsers(usersResponse.data || []);
        // Update pagination information from response
        if (usersResponse.pagination) {
          setPagination(prev => ({
            ...prev,
            ...usersResponse.pagination
          }));
        } else {
          // Fallback
          setPagination(prev => ({
            ...prev,
            total: usersResponse.data?.length || 0,
            pages: Math.ceil((usersResponse.data?.length || 0) / prev.limit)
          }));
        }
      } else {
        throw new Error(usersResponse.message || 'Failed to fetch users');
      }

      if (companiesResponse.success) {
        setCompanies(companiesResponse.data || []);
      } else {
        throw new Error(companiesResponse.message || 'Failed to fetch companies');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Erreur lors du chargement des données. Veuillez réessayer plus tard.');

      // Use mock data in development for testing
      if (process.env.NODE_ENV === 'development') {
        setUsers([
          { _id: '1', name: 'John Doe', email: 'john@example.com', role: 'admin', companyName: 'Entreprise A', companyId: '1', status: 'active', lastLogin: '2025-03-15T09:30:00' },
          { _id: '2', name: 'Jane Smith', email: 'jane@example.com', role: 'admin', companyName: 'Entreprise B', companyId: '2', status: 'active', lastLogin: '2025-03-17T14:45:00' },
          { _id: '3', name: 'Bob Johnson', email: 'bob@example.com', role: 'admin', companyName: 'Entreprise C', companyId: '3', status: 'inactive', lastLogin: null }
        ]);

        setCompanies([
          { _id: '1', name: 'Entreprise A' },
          { _id: '2', name: 'Entreprise B' },
          { _id: '3', name: 'Entreprise C' }
        ]);

        setPagination({
          page: 1,
          limit: 10,
          total: 3,
          pages: 1
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort users
  // Note: Filtering is now largely handled on the server side
  const filteredUsers = [...users]
    .sort((a, b) => {
      if (!sortConfig.key) return 0;

      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key === 'lastLogin') {
        if (!aValue && !bValue) return 0;
        if (!aValue) return sortConfig.direction === 'asc' ? 1 : -1;
        if (!bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      return 0;
    });

  // Calculate metrics
  const totalUsersMetric = pagination.total || users.length;
  const activeUsersCount = users.filter(user => user.status === 'active').length;
  const superAdminCount = users.filter(user => user.role === 'superadmin').length;
  const providerCount = users.filter(user => user.role === 'admin').length;
  const analystCount = users.filter(user => user.role === 'simpleuser').length;

  const metrics = [
    {
      title: 'Total Utilisateurs',
      value: totalUsersMetric,
      bgColor: 'bg-blue-100',
      icon: (
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      title: 'Utilisateurs Actifs',
      value: activeUsersCount,
      bgColor: 'bg-green-100',
      icon: (
        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: 'SuperAdmins',
      value: superAdminCount,
      bgColor: 'bg-purple-100',
      icon: (
        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: 'Providers',
      value: providerCount,
      bgColor: 'bg-indigo-100',
      icon: (
        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
        </svg>
      )
    }
  ];

  const handleInputChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePasswordChange = (name, value) => {
    setPasswordData({
      ...passwordData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare data for API
      const userData = { ...formData };

      if (isEditing) {
        // For updates, handle password separately if provided
        const hasPasswordUpdate = userData.password && userData.password.trim();

        if (hasPasswordUpdate && !validatePassword(userData.password).isValid) {
          setError('Le mot de passe doit comporter au moins 8 caractères, une majuscule, une minuscule, un chiffre et un symbole.');
          setIsSubmitting(false);
          return;
        }

        // Remove password from main user data - it must go through /password endpoint
        delete userData.password;

        // Update existing user
        const response = await userService.updateUser(currentUserId, userData);

        if (response.success) {
          // If password was provided, update it separately
          if (hasPasswordUpdate) {
            try {
              await userService.updatePassword(currentUserId, {
                newPassword: formData.password
              });
            } catch (passwordError) {
              console.error('Password update error:', passwordError);
              setError('Utilisateur mis à jour, mais erreur lors du changement de mot de passe.');
              return;
            }
          }

          // Update the user in the local state
          setUsers(
            users.map((user) =>
              user._id === currentUserId
                ? { ...user, ...response.data }
                : user
            )
          );

          resetForm();
        } else {
          throw new Error(response.message || 'Failed to update user');
        }
      } else {
        // Validate password on creation
        if (!userData.password || !validatePassword(userData.password).isValid) {
          setError('Le mot de passe doit comporter au moins 8 caractères, une majuscule, une minuscule, un chiffre et un symbole.');
          setIsSubmitting(false);
          return;
        }

        // Create new user
        const response = await userService.createUser(userData);

        if (response.success) {
          // Add the new user to the local state or refresh the list
          fetchData();
          resetForm();
        } else {
          throw new Error(response.message || 'Failed to create user');
        }
      }
    } catch (error) {
      console.error(isEditing ? 'Update user error:' : 'Create user error:', error);
      setError(`Erreur lors de ${isEditing ? 'la mise à jour' : 'la création'} de l'utilisateur. ${error.message || 'Veuillez réessayer.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!passwordData.newPassword || !validatePassword(passwordData.newPassword).isValid) {
      setError('Le nouveau mot de passe doit comporter au moins 8 caractères, une majuscule, une minuscule, un chiffre et un symbole.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await userService.updatePassword(currentUserId, passwordData);

      if (response.success) {
        setShowPasswordModal(false);
        setPasswordData({
          currentPassword: '',
          newPassword: ''
        });
        alert('Mot de passe mis à jour avec succès');
      } else {
        throw new Error(response.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Update password error:', error);
      setError(`Erreur lors de la mise à jour du mot de passe. ${error.message || 'Veuillez réessayer.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    if (!user || typeof user !== 'object') {
      console.error('Invalid user object:', user);
      return;
    }

    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '', // Clear password field when editing
      role: user.role || 'admin',
      companyId: user.companyId || '',
      status: user.status || 'active'
    });

    setCurrentUserId(user._id);
    setIsEditing(true);
  };

  const handlePasswordModalOpen = (user) => {
    setCurrentUserId(user._id);
    setShowPasswordModal(true);
  };

  const handleStatusToggle = async (user) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir ${user.status === 'active' ? 'désactiver' : 'activer'} l'utilisateur ${user.name}?`)) {
      return;
    }

    setIsLoading(true);

    try {
      const updatedStatus = user.status === 'active' ? 'inactive' : 'active';

      const response = await userService.updateUser(user._id, { status: updatedStatus });

      if (response.success) {
        setUsers(
          users.map((u) =>
            u._id === user._id
              ? { ...u, status: updatedStatus }
              : u
          )
        );
      } else {
        throw new Error(response.message || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Update user status error:', error);
      setError('Erreur lors de la mise à jour du statut. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setIsLoading(true);
    setShowConfirmation(false);

    try {
      const response = await userService.deleteUser(userToDelete._id);

      if (response.success) {
        // Remove the user from the local state
        setUsers(users.filter(u => u._id !== userToDelete._id));
        setUserToDelete(null);
      } else {
        throw new Error(response.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete user error:', error);
      setError('Erreur lors de la suppression de l\'utilisateur. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'admin',
      companyId: '',
      status: 'active'
    });
    setIsEditing(false);
    setCurrentUserId(null);
    setError(null);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    switch (key) {
      case 'role':
        setRoleFilter(value);
        break;
      case 'status':
        setStatusFilter(value);
        break;
      case 'company':
        setCompanyFilter(value);
        break;
      default:
        break;
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setStatusFilter('all');
    setCompanyFilter('all');
  };

  // Pagination controls
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // View user details
  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  // Table columns definition
  const columns = [
    {
      key: 'name',
      label: 'Nom',
      sortable: true,
      render: (value) => <span className="font-medium text-gray-900 text-sm">{value || '-'}</span>
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value || '-'}</span>
    },
    {
      key: 'role',
      label: 'Rôle',
      sortable: true,
      render: (value) => <RoleBadge role={value} size="sm" />
    },
    {
      key: 'companyName',
      label: 'Entreprise',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value || '-'}</span>
    },
    {
      key: 'status',
      label: 'Statut',
      sortable: true,
      render: (value) => <StatusBadge status={value === 'active' ? 'Actif' : 'Inactif'} type={value === 'active' ? 'success' : 'danger'} size="sm" />
    },
    {
      key: 'cguAccepted',
      label: 'CGU',
      sortable: true,
      render: (value) => (
        <div className="flex items-center justify-center text-sm font-bold" title={value ? 'Conditions acceptées' : 'Conditions non acceptées'}>
          {value ? (
            <span className="text-green-700 bg-green-100 border border-green-200 px-2 py-1 rounded-md flex items-center shadow-sm">
              <span className="mr-1">✅</span> Oui
            </span>
          ) : (
            <span className="text-gray-600 bg-gray-100 border border-gray-200 px-2 py-1 rounded-md flex items-center shadow-sm">
              <span className="mr-1">❌</span> Non
            </span>
          )}
        </div>
      )
    },
    {
      key: 'lastLogin',
      label: 'Dernière connexion',
      sortable: true,
      render: (value) => <span className="text-gray-500 text-sm">{value ? new Date(value).toLocaleString() : 'Jamais'}</span>
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, user) => (
        <div className="flex flex-wrap gap-1">
          <button
            onClick={() => handleViewDetails(user)}
            className="text-xs text-gray-600 hover:text-gray-900 font-medium px-2 py-1"
            disabled={isLoading}
          >
            Détails
          </button>

          <button
            onClick={() => handleEdit(user)}
            className="text-xs text-indigo-600 hover:text-indigo-900 font-medium px-2 py-1"
            disabled={isLoading || user.role === 'superadmin'}
          >
            Modifier
          </button>

          <button
            onClick={() => handlePasswordModalOpen(user)}
            className="text-xs text-blue-600 hover:text-blue-900 font-medium px-2 py-1"
            disabled={isLoading || user.role === 'superadmin'}
          >
            Mot de passe
          </button>

          <button
            onClick={() => handleStatusToggle(user)}
            className={
              user.status === 'active'
                ? 'text-xs text-red-600 hover:text-red-900 font-medium px-2 py-1'
                : 'text-xs text-green-600 hover:text-green-900 font-medium px-2 py-1'
            }
            disabled={isLoading || user.role === 'superadmin'}
          >
            {user.status === 'active' ? 'Désactiver' : 'Activer'}
          </button>

          <button
            onClick={() => handleDeleteClick(user)}
            className="text-xs text-red-600 hover:text-red-900 font-medium px-2 py-1"
            disabled={isLoading || user.role === 'superadmin'}
          >
            Supprimer
          </button>

          <Link
            to={`/superadmin/activities?userId=${user._id}`}
            className="text-xs text-blue-600 hover:text-blue-900 font-medium px-2 py-1"
          >
            Activités
          </Link>
        </div>
      )
    }
  ];

  // Form fields definition
  const formFields = [
    {
      name: 'name',
      label: 'Nom complet',
      type: 'text',
      required: true
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true
    },
    {
      name: 'password',
      label: isEditing ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe',
      type: 'password',
      required: !isEditing
    },
    {
      name: 'role',
      label: 'Rôle',
      type: 'select',
      options: [
        { value: 'admin', label: 'Provider (Admin Entreprise)' },
        { value: 'simpleuser', label: 'Analyste' }
      ]
    },
    {
      name: 'companyId',
      label: 'Entreprise',
      type: 'select',
      required: true,
      options: [
        { value: '', label: '-- Sélectionner une entreprise --' },
        ...companies.map(company => ({
          value: company._id,
          label: company.name
        }))
      ]
    },
    {
      name: 'status',
      label: 'Statut',
      type: 'select',
      options: [
        { value: 'active', label: 'Actif' },
        { value: 'inactive', label: 'Inactif' }
      ]
    }
  ];

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Gestion des Utilisateurs</h1>
        <div className="text-xs text-gray-500">
          Total: {totalUsersMetric} utilisateurs
        </div>
      </div>

      {/* Metrics Summary */}
      <MetricsSummary metrics={metrics} />

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-4 text-sm">
          <p>{error}</p>
          <button
            onClick={fetchData}
            className="mt-1 text-red-800 hover:text-red-900 font-medium text-sm"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Search and Filter Controls */}
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filters={[
          {
            key: 'role',
            label: 'Filtrer par rôle',
            type: 'select',
            value: roleFilter,
            options: [
              { value: 'all', label: 'Tous les rôles' },
              { value: 'superadmin', label: 'SuperAdmin' },
              { value: 'admin', label: 'Provider' },
              { value: 'simpleuser', label: 'Analyste' }
            ]
          },
          {
            key: 'status',
            label: 'Filtrer par statut',
            type: 'select',
            value: statusFilter,
            options: [
              { value: 'all', label: 'Tous les statuts' },
              { value: 'active', label: 'Actif' },
              { value: 'inactive', label: 'Inactif' }
            ]
          },
          {
            key: 'company',
            label: 'Filtrer par entreprise',
            type: 'select',
            value: companyFilter,
            options: [
              { value: 'all', label: 'Toutes les entreprises' },
              ...companies.map(company => ({
                value: company._id,
                label: company.name
              }))
            ]
          }
        ]}
        onFilterChange={handleFilterChange}
        onResetFilters={resetFilters}
        placeholder="Nom ou email..."
      />

      {/* User Form */}
      <FormBuilder
        title={isEditing ? 'Modifier un utilisateur' : 'Ajouter un utilisateur'}
        fields={formFields}
        formData={formData}
        onChange={handleInputChange}
        onSubmit={handleSubmit}
        onCancel={isEditing ? resetForm : null}
        isEditing={isEditing}
        isSubmitting={isSubmitting}
        submitLabel={isEditing ? 'Mettre à jour' : 'Ajouter'}
        cancelLabel="Annuler"
      />

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg w-full max-w-md">
            <h2 className="text-base font-semibold mb-3">Changer le mot de passe</h2>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-3 text-sm">
                <p>{error}</p>
              </div>
            )}

            <form onSubmit={handlePasswordSubmit}>
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Mot de passe actuel
                </label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2"
                  disabled={isSubmitting}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Requis uniquement si vous modifiez votre propre mot de passe
                </p>
              </div>

              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Nouveau mot de passe
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  required
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:ring-2"
                  disabled={isSubmitting}
                />
                {passwordData.newPassword && (
                  <PasswordRequirements password={passwordData.newPassword} />
                )}
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-3 py-1.5 text-sm bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Chargement...' : 'Mettre à jour'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users list */}
      <DataTable
        data={filteredUsers}
        columns={columns}
        isLoading={isLoading}
        onSort={handleSort}
        sortConfig={sortConfig}
        emptyMessage="Aucun utilisateur trouvé. Ajoutez votre premier utilisateur ci-dessus."
        searchQuery={searchTerm}
        filters={{ role: roleFilter, status: statusFilter, company: companyFilter }}
      />

      {/* Pagination */}
      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.pages}
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        onPageChange={handlePageChange}
        isLoading={isLoading}
      />

      {/* User Details Modal */}
      {showUserDetails && (
        <UserDetails
          user={selectedUser}
          onClose={() => setShowUserDetails(false)}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Supprimer l'utilisateur"
        message={`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userToDelete?.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        confirmButtonClass="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        cancelButtonClass="px-3 py-1.5 text-sm bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
      />
    </div>
  );
};

export default UserManagement;